import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../database/config';

// A simple icon component for demonstration.
// In a real app, you would use a library like react-icons.
const Icon = ({ path }) => (
  <svg className="w-6 h-6 mr-3 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={path}></path>
  </svg>
);

const Section = ({ title, iconPath, children }) => (
  <div className="mb-8">
    <div className="flex items-center mb-3">
      {iconPath && <Icon path={iconPath} />}
      <h2 className="text-xl font-semibold text-gray-800">{title}</h2>
    </div>
    <div className="pl-9 text-gray-600 leading-relaxed">
      {children}
    </div>
  </div>
);

const TermsAndConditionsPage = () => {
  const [headerData, setHeaderData] = useState(() => {
    try {
      const cached = localStorage.getItem('bento_terms_data');
      return cached ? JSON.parse(cached).headerData : { H1: 'Terms & Conditions', para: '' };
    } catch {
      return { H1: 'Terms & Conditions', para: '' };
    }
  });

  const [mainData, setMainData] = useState(() => {
    try {
      const cached = localStorage.getItem('bento_terms_data');
      return cached ? JSON.parse(cached).mainData : {};
    } catch {
      return {};
    }
  });

  const [ctaData, setCtaData] = useState(() => {
    try {
      const cached = localStorage.getItem('bento_terms_data');
      return cached ? JSON.parse(cached).ctaData : { h3: 'Still have questions?', para: 'Our support team is ready to help.', '<a>': 'Contact Support' };
    } catch {
      return { h3: 'Still have questions?', para: 'Our support team is ready to help.', '<a>': 'Contact Support' };
    }
  });

  const [loading, setLoading] = useState(() => {
    return !localStorage.getItem('bento_terms_data');
  });

  useEffect(() => {
    const fetchTermsData = async () => {
      try {
        const [headerSnap, mainSnap, ctaSnap] = await Promise.all([
           getDoc(doc(db, 'termsandconditions', 'Header')),
           getDoc(doc(db, 'termsandconditions', 'main')),
           getDoc(doc(db, 'termsandconditions', 'Contact CTA Section'))
        ]);
        
        let newHeader = headerData;
        let newMain = mainData;
        let newCta = ctaData;

        if (headerSnap.exists()) {
          newHeader = headerSnap.data();
          setHeaderData(newHeader);
        }
        if (mainSnap.exists()) {
          newMain = mainSnap.data();
          setMainData(newMain);
        }
        if (ctaSnap.exists()) {
          newCta = ctaSnap.data();
          setCtaData(newCta);
        }
        
        localStorage.setItem('bento_terms_data', JSON.stringify({
            headerData: newHeader,
            mainData: newMain,
            ctaData: newCta
        }));

      } catch (error) {
        console.error('Error fetching Terms and Conditions data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTermsData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 font-sans p-4 sm:p-6 md:p-10 flex items-center justify-center">
        <div className="animate-pulse text-gray-500 text-lg">Loading...</div>
      </div>
    );
  }

  return(
    <div className="!pt-28 md:!pt-32 bg-gray-100 font-sans p-4 sm:p-6 md:p-10">
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="p-8 sm:p-12">
          <header className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">{headerData.H1}</h1>
            <p className="mt-4 text-lg text-gray-500">{headerData.para}</p>
          </header>

          <main>
            {Object.keys(mainData)
              .sort((a, b) => parseInt(a.match(/\d+/)?.[0] || 0) - parseInt(b.match(/\d+/)?.[0] || 0))
              .map((key) => {
                const content = mainData[key] || '';
                const parts = content.split('$');
                const iconPath = parts.length === 3 ? parts[0] : '';
                const title = parts.length === 3 ? parts[1] : '';
                const description = parts.length === 3 ? parts[2] : content;

                return (
                  <Section key={key} title={title} iconPath={iconPath}>
                    <div className="text-justify">{description}</div>
                  </Section>
                );
              })}
          </main>
        </div>
      </div>
      {/* Contact CTA Section */}
      <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden mt-10 justify-center p-4 max-w-4xl mx-auto mt-10 p-5 md:p-6 rounded-lg text-center">
        <h3 className="text-base md:text-lg font-bold text-slate-900">{ctaData.h3}</h3>
        <p className="mt-1 text-sm md:text-sm text-slate-600">{ctaData.para}</p>
        <a
          href="mailto:support@getbentobox.in"
          className="inline-block mt-3 px-5 py-1.5 bg-indigo-600 text-white font-semibold text-md md:text-sm rounded-md shadow-sm hover:bg-indigo-700"
        >
          {ctaData['<a>']}
        </a>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage; 